/**
 * The one query-key factory (ADR 0006). Every key starts with the account id,
 * so signing out or switching accounts clears exactly that account's cache.
 */
export const qk = {
  account: (account: string) => ['account', account] as const,
  me: (account: string) => ['account', account, 'me'] as const,
  branding: () => ['branding'] as const,
  folders: (account: string) => ['account', account, 'mail', 'folders'] as const,
  tags: (account: string) => ['account', account, 'mail', 'tags'] as const,
  conversations: (account: string, folderId: string, filters?: Record<string, unknown>) =>
    ['account', account, 'mail', 'conversations', folderId, filters ?? {}] as const,
  /** Prefix of every conversation list — invalidate it after an action that moves mail. */
  conversationLists: (account: string) => ['account', account, 'mail', 'conversations'] as const,
  conversation: (account: string, id: string) =>
    ['account', account, 'mail', 'conversation', id] as const,
  message: (account: string, id: string) => ['account', account, 'mail', 'message', id] as const,
  search: (account: string, params: Record<string, unknown>) =>
    ['account', account, 'mail', 'search', params] as const,
  autocomplete: (account: string, query: string) =>
    ['account', account, 'mail', 'autocomplete', query] as const,
  signatures: (account: string) => ['account', account, 'mail', 'signatures'] as const,
  blockedSenders: (account: string) => ['account', account, 'mail', 'blocked-senders'] as const,
  /** The mailbox's rules, in the order they run (one cache entry: the server has one list). */
  mailRules: (account: string) => ['account', account, 'mail', 'rules'] as const,
  drafts: (account: string) => ['account', account, 'mail', 'drafts'] as const,
  /** Delayed sends the gateway still holds for the account (server-side undo send). */
  pendingSends: (account: string) => ['account', account, 'mail', 'sends'] as const,
  calendars: (account: string) => ['account', account, 'calendar', 'calendars'] as const,
  events: (account: string, start: string, end: string) =>
    ['account', account, 'calendar', 'events', start, end] as const,
  event: (account: string, id: string, instanceStart?: string) =>
    ['account', account, 'calendar', 'event', id, instanceStart ?? ''] as const,
  contactFolders: (account: string) => ['account', account, 'contacts', 'folders'] as const,
  contacts: (account: string, params: Record<string, unknown>) =>
    ['account', account, 'contacts', 'list', params] as const,
  contact: (account: string, id: string) => ['account', account, 'contacts', 'item', id] as const,
  tasks: (account: string, params: Record<string, unknown>) =>
    ['account', account, 'tasks', 'list', params] as const,
  task: (account: string, id: string) => ['account', account, 'tasks', 'item', id] as const,
  accountInfo: (account: string) => ['account', account, 'settings', 'account'] as const,
  prefs: (account: string) => ['account', account, 'settings', 'prefs'] as const,
  outOfOffice: (account: string) => ['account', account, 'settings', 'ooo'] as const,
  /** The addresses the mailbox may write from, with the identities built on them. */
  identities: (account: string) => ['account', account, 'settings', 'identities'] as const,
  forwarding: (account: string) => ['account', account, 'settings', 'forwarding'] as const,
  sessions: (account: string) => ['account', account, 'sessions'] as const,
  devices: (account: string) => ['account', account, 'devices'] as const,
} as const;
